/**
 * Sentry frontend init. No Replay (PostHog owns session recording).
 * beforeSend scrubs PII and forwards error to PostHog via optional ref.
 * email is allowed in Sentry user context for support/debugging.
 */
import * as Sentry from '@sentry/react';

const PII_KEYS = ['name', 'firstName', 'lastName', 'username'];

/** Ref set by AuthContext so beforeSend can call PostHog when available. */
let posthogForSentry: { capture: (event: string, props?: Record<string, unknown>) => void } | null = null;

export function setPostHogForSentry(
  ph: { capture: (event: string, props?: Record<string, unknown>) => void } | null
): void {
  posthogForSentry = ph;
}

function scrubPII(obj: Record<string, unknown> | undefined): void {
  if (!obj || typeof obj !== 'object') return;
  for (const key of PII_KEYS) {
    if (key in obj && obj[key] !== undefined) {
      obj[key] = '[REDACTED]';
    }
  }
}

/** Local / Vite dev should not send events (avoids alert noise). Opt back in with VITE_SENTRY_ENABLE_IN_DEV=true. */
function isSentryDisabledForLocalDev(): boolean {
  if (import.meta.env.VITE_SENTRY_ENABLE_IN_DEV === 'true') {
    return false;
  }
  if (import.meta.env.DEV) {
    return true;
  }
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    if (h === 'localhost' || h === '127.0.0.1') {
      return true;
    }
  }
  return false;
}

export function initSentry(): void {
  if (isSentryDisabledForLocalDev()) {
    if (import.meta.env.DEV) {
      console.debug(
        '[Sentry] Disabled on local/dev (set VITE_SENTRY_ENABLE_IN_DEV=true to send from this machine)'
      );
    }
    return;
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn || typeof dsn !== 'string' || !dsn.startsWith('https://')) {
    if (import.meta.env.DEV) {
      console.debug('[Sentry] Not initializing: VITE_SENTRY_DSN missing or invalid');
    }
    return;
  }

  try {
    Sentry.init({
      dsn,
      environment:
        (import.meta.env.VITE_SENTRY_ENVIRONMENT as string) ||
        import.meta.env.MODE ||
        'development',
      release:
        (import.meta.env.VITE_APP_VERSION as string) ||
        (import.meta.env.VITE_SENTRY_RELEASE as string) ||
        undefined,
      // Do not set sendDefaultPii: we scrub PII in beforeSend (PostHog owns session recording).
      integrations: [Sentry.browserTracingIntegration()],
      // Vercel Live Feedback widget can throw e.g. InvalidNodeTypeError (selectNode on detached node); ignore its script.
      denyUrls: [/vercel\.live\/_next-live\//],
      ignoreErrors: [
        /Failed to execute 'selectNode' on 'Range': the given Node has no parent\./,
      ],
      tracesSampleRate: 0.2,
      tracePropagationTargets: [
        'localhost',
        /^https:\/\/api\.edgetechnologies\.com\.au\/api/,
        /^https:\/\/api\.edgetechnologies\.com\.au/,
      ],
      beforeSend(event) {
        if (event.user) scrubPII(event.user as Record<string, unknown>);
        if (event.extra) scrubPII(event.extra as Record<string, unknown>);
        if (event.request?.headers) {
          const headers = event.request.headers as Record<string, string>;
          for (const k of Object.keys(headers)) {
            if (/email|name|authorization|cookie|token/i.test(k)) {
              headers[k] = '[REDACTED]';
            }
          }
        }
        try {
          if (posthogForSentry) {
            posthogForSentry.capture('sentry_error', {
              error_message: event.exception?.values?.[0]?.value,
              sentry_event_id: event.event_id,
            });
          }
        } catch {
          // ignore PostHog capture failures
        }
        return event;
      },
    });
    if (import.meta.env.DEV) {
      console.debug('[Sentry] Initialized', { dsn: dsn.slice(0, 50) + '...', environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string) || import.meta.env.MODE });
    }
  } catch (e) {
    if (import.meta.env.DEV) {
      console.error('[Sentry] Init failed:', e);
    }
  }
}
